import type { 
  IOnboardingRepository, 
  OnboardingFlow, 
  StalledOnboardingFlow, 
  OnboardingInvitation, 
  DetailedOnboardingFlow,
  OnboardingStep,
  OnboardingActivity
} from '../interfaces/IOnboardingRepository';
import { mockOnboardingFlows, mockClients, mockUsers, simulateDelay, maybeSimulateError } from './mockData';

export class MockOnboardingRepository implements IOnboardingRepository {
  private flows: OnboardingFlow[] = [...mockOnboardingFlows];
  private invitations: OnboardingInvitation[] = [];

  async getByToken(token: string): Promise<OnboardingFlow | null> {
    await simulateDelay();
    maybeSimulateError();
    return this.flows.find(f => f.token === token) || null;
  }

  async getById(flowId: string): Promise<DetailedOnboardingFlow | null> {
    await simulateDelay();
    maybeSimulateError();

    const flow = this.flows.find(f => f.id === flowId);
    if (!flow) return null;

    const client = mockClients.find(t => t.id === flow.tenant_id);
    const accountManager = mockUsers.find(u => u.client_id === flow.tenant_id && u.role === 'account_manager');
    
    const now = new Date();
    const created = new Date(flow.created_at);
    const days_stalled = Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));
    
    // Determine risk level
    let risk: 'low' | 'medium' | 'high' | 'critical' = 'low';
    if (days_stalled >= 21) risk = 'critical';
    else if (days_stalled >= 14) risk = 'high';
    else if (days_stalled >= 7) risk = 'medium';
    
    // Determine missing roles
    const missing_roles: string[] = [];
    if (!flow.account_manager_completed) missing_roles.push('Account Manager');
    if (!flow.finance_manager_completed) missing_roles.push('Finance Manager');
    if (!flow.creative_manager_completed) missing_roles.push('Creator Manager');
    if (!flow.account_owner_completed) missing_roles.push('Account Owner');

    // Build steps
    const steps: OnboardingStep[] = [
      {
        role: 'Account Manager',
        completed: flow.account_manager_completed,
        completed_at: flow.account_manager_completed ? flow.created_at : undefined,
        data: { company_data: true, brands: 2 }
      },
      {
        role: 'Finance Manager',
        completed: flow.finance_manager_completed,
        completed_at: flow.finance_manager_completed ? flow.created_at : undefined,
        data: { vat_validated: flow.finance_manager_completed }
      },
      {
        role: 'Creator Manager',
        completed: flow.creative_manager_completed,
        completed_at: flow.creative_manager_completed ? flow.created_at : undefined,
      },
      {
        role: 'Account Owner',
        completed: flow.account_owner_completed,
        completed_at: flow.account_owner_completed ? flow.created_at : undefined,
        data: { admin_unlocked: flow.admin_unlocked }
      },
    ];

    // Build activities
    const activities: OnboardingActivity[] = [
      {
        id: '1',
        flow_id: flowId,
        activity_type: 'status_change',
        description: 'Onboarding flow started',
        created_at: flow.created_at,
      },
      ...flow.reminders_sent.map((reminder, idx) => ({
        id: `reminder-${idx}`,
        flow_id: flowId,
        activity_type: 'reminder_sent' as const,
        description: `Reminder sent: ${reminder.template}`,
        created_at: reminder.sent_at,
      })),
    ];

    if (flow.account_manager_completed) {
      activities.push({
        id: 'am-complete',
        flow_id: flowId,
        activity_type: 'role_completed',
        description: 'Account Manager completed their section',
        created_at: flow.created_at,
      });
    }

    return {
      ...flow,
      tenant_name: client?.name || 'Unknown Client',
      account_manager_email: accountManager?.email || 'unknown@example.com',
      days_stalled,
      last_activity: flow.last_reminder_sent_at || flow.created_at,
      missing_roles,
      risk_level: risk,
      notes: flow.admin_notes,
      steps,
      activities: activities.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ),
      invitations: this.invitations.filter(i => i.flow_id === flowId),
      // Board resolution fields - mock data
      board_resolution_required: Math.random() > 0.7,
      board_resolution_uploaded: Math.random() > 0.5,
      board_resolution_file_url: Math.random() > 0.5 ? 'https://example.com/board-resolution.pdf' : undefined,
      board_resolution_verified_by: Math.random() > 0.6 ? 'admin-user-id' : undefined,
      board_resolution_verified_at: Math.random() > 0.6 ? new Date().toISOString() : undefined,
      board_resolution_verification_notes: Math.random() > 0.6 ? 'Document verified and approved' : undefined,
      director_name: 'John Smith',
      director_id_number: 'MT123456A',
    };
  }

  async getAll(tenantId: string): Promise<OnboardingFlow[]> {
    await simulateDelay();
    maybeSimulateError();
    return this.flows.filter(f => f.tenant_id === tenantId);
  }

  async getAllFlows(_platformTenantId?: string): Promise<OnboardingFlow[]> {
    await simulateDelay();
    maybeSimulateError();
    // For mock, return all flows or filter by platform if needed
    return this.flows;
  }

  async resetFlow(flowId: string, reset_by: string): Promise<void> {
    await simulateDelay();
    maybeSimulateError();

    const index = this.flows.findIndex(f => f.id === flowId);
    if (index === -1) throw new Error('Flow not found');

    this.flows[index] = {
      ...this.flows[index],
      status: 'pending',
      account_manager_completed: false,
      finance_manager_completed: false,
      creative_manager_completed: false,
      account_owner_completed: false,
      overall_progress: 0,
      admin_unlocked: false,
      reminder_count: 0,
      last_reminder_sent_at: undefined,
      reminders_sent: [],
      admin_notes: `Flow reset by ${reset_by} at ${new Date().toISOString()}`,
    };
  }

  async duplicateFlow(flowId: string, created_by: string): Promise<OnboardingFlow> {
    await simulateDelay();
    maybeSimulateError();

    const original = this.flows.find(f => f.id === flowId);
    if (!original) throw new Error('Flow not found');

    const duplicated: OnboardingFlow = {
      ...original,
      id: crypto.randomUUID(),
      token: crypto.randomUUID(),
      magic_token: crypto.randomUUID(),
      status: 'pending',
      created_at: new Date().toISOString(),
      completed_at: undefined,
      account_manager_completed: false,
      account_manager_completed_at: undefined,
      finance_manager_completed: false,
      finance_manager_completed_at: undefined,
      creative_manager_completed: false,
      creative_manager_completed_at: undefined,
      account_owner_completed: false,
      account_owner_completed_at: undefined,
      overall_progress: 0,
      admin_unlocked: false,
      reminder_count: 0,
      last_reminder_sent_at: undefined,
      reminders_sent: [],
      is_test_flow: true,
      created_via: 'admin_duplicate',
    };

    this.flows.push(duplicated);
    // Flow duplicated
    return duplicated;
  }

  async getStalledFlows(riskLevel?: 'low' | 'medium' | 'high' | 'critical'): Promise<StalledOnboardingFlow[]> {
    await simulateDelay();
    maybeSimulateError();

    const stalled = this.flows.filter(f => 
      f.status === 'in_progress' && !f.admin_unlocked
    );

    // Enrich flows with tenant and user data
    const enrichedFlows: StalledOnboardingFlow[] = stalled.map(f => {
      const client = mockClients.find(t => t.id === f.tenant_id);
      const accountManager = mockUsers.find((u: any) => u.client_id === f.tenant_id && u.role === 'account_manager');
      
      const now = new Date();
      const created = new Date(f.created_at);
      const days_stalled = Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));
      
      // Determine risk level
      let risk: 'low' | 'medium' | 'high' | 'critical' = 'low';
      if (days_stalled >= 21) risk = 'critical';
      else if (days_stalled >= 14) risk = 'high';
      else if (days_stalled >= 7) risk = 'medium';
      
      // Determine missing roles
      const missing_roles: string[] = [];
      if (!f.account_manager_completed) missing_roles.push('Account Manager');
      if (!f.finance_manager_completed) missing_roles.push('Finance Manager');
      if (!f.creative_manager_completed) missing_roles.push('Creator Manager');
      if (!f.account_owner_completed) missing_roles.push('Account Owner');
      
      return {
        ...f,
        tenant_name: client?.name || 'Unknown Client',
        account_manager_email: accountManager?.email || 'unknown@example.com',
        days_stalled,
        last_activity: f.last_reminder_sent_at || f.created_at,
        missing_roles,
        risk_level: risk,
        notes: f.admin_notes,
      };
    });

    if (!riskLevel) return enrichedFlows;

    // Filter by risk level
    return enrichedFlows.filter(f => f.risk_level === riskLevel);
  }

  async createFlow(data: { 
    client_id: string; 
    created_by: string;
    is_test_flow?: boolean;
    created_via?: string;
  }): Promise<OnboardingFlow> {
    await simulateDelay();
    maybeSimulateError();

    const flow: OnboardingFlow = {
      id: crypto.randomUUID(),
      tenant_id: data.client_id,
      client_id: data.client_id,
      flow_type: 'client',
      token: crypto.randomUUID(),
      magic_token: crypto.randomUUID(),
      primary_email: 'test@example.com',
      status: 'pending',
      created_at: new Date().toISOString(),
      account_manager_completed: false,
      finance_manager_completed: false,
      creative_manager_completed: false,
      account_owner_completed: false,
      overall_progress: 0,
      admin_unlocked: false,
      reminder_count: 0,
      reminders_sent: [],
      is_test_flow: data.is_test_flow || false,
      created_via: data.created_via || 'api',
    };

    this.flows.push(flow);
    return flow;
  }

  async updateProgress(flowId: string, data: {
    account_manager_completed?: boolean;
    finance_manager_completed?: boolean;
    creative_manager_completed?: boolean;
    account_owner_completed?: boolean;
  }): Promise<OnboardingFlow> {
    await simulateDelay();
    maybeSimulateError();

    const index = this.flows.findIndex(f => f.id === flowId);
    if (index === -1) throw new Error('Flow not found');

    const flow = this.flows[index];
    const updated = { ...flow, ...data };

    // Calculate overall progress
    const completed = [
      updated.account_manager_completed,
      updated.finance_manager_completed,
      updated.creative_manager_completed,
      updated.account_owner_completed,
    ].filter(Boolean).length;

    updated.overall_progress = (completed / 4) * 100;
    updated.admin_unlocked = updated.account_owner_completed;
    
    if (updated.overall_progress === 100) {
      updated.status = 'completed';
      updated.completed_at = new Date().toISOString();
    } else {
      updated.status = 'in_progress';
    }

    this.flows[index] = updated;
    return updated;
  }

  async completeRole(flowId: string, role: string, completed_by: string): Promise<void> {
    await simulateDelay();
    maybeSimulateError();

    const roleMap: Record<string, keyof Pick<OnboardingFlow, 
      'account_manager_completed' | 'finance_manager_completed' | 
      'creative_manager_completed' | 'account_owner_completed'>> = {
      'account_manager': 'account_manager_completed',
      'finance_manager': 'finance_manager_completed',
      'creative_manager': 'creative_manager_completed',
      'account_owner': 'account_owner_completed',
    };

    const field = roleMap[role];
    if (!field) throw new Error('Invalid role');

    await this.updateProgress(flowId, { [field]: true });
    // Role completed
  }

  async cancelFlow(flowId: string, cancelled_by: string, reason: string): Promise<void> {
    await simulateDelay();
    maybeSimulateError();

    const index = this.flows.findIndex(f => f.id === flowId);
    if (index === -1) throw new Error('Flow not found');

    this.flows[index].status = 'cancelled';
    this.flows[index].admin_notes = `Cancelled by ${cancelled_by}: ${reason}`;
  }

  async inviteRoleMember(data: {
    flow_id: string;
    email: string;
    role: string;
    invited_by: string;
  }): Promise<OnboardingInvitation> {
    await simulateDelay();
    maybeSimulateError();

    const invitation: OnboardingInvitation = {
      id: crypto.randomUUID(),
      flow_id: data.flow_id,
      email: data.email,
      role: data.role,
      invited_by: data.invited_by,
      invited_at: new Date().toISOString(),
      accepted: false,
      completed: false,
    };

    this.invitations.push(invitation);
    return invitation;
  }

  async getFlowInvitations(flowId: string): Promise<OnboardingInvitation[]> {
    await simulateDelay();
    maybeSimulateError();
    return this.invitations.filter(i => i.flow_id === flowId);
  }

  async sendReminder(flowId: string, template: string, sent_by?: string): Promise<void> {
    await simulateDelay();
    maybeSimulateError();

    const index = this.flows.findIndex(f => f.id === flowId);
    if (index === -1) throw new Error('Flow not found');

    const flow = this.flows[index];
    flow.reminders_sent.push({
      template,
      sent_at: new Date().toISOString(),
    });
    flow.reminder_count++;
    flow.last_reminder_sent_at = new Date().toISOString();

    // Reminder sent
  }

  async updateAdminNotes(flowId: string, notes: string, updated_by: string): Promise<void> {
    await simulateDelay();
    maybeSimulateError();

    const index = this.flows.findIndex(f => f.id === flowId);
    if (index === -1) throw new Error('Flow not found');

    this.flows[index].admin_notes = notes;
    // Admin notes updated
  }
}
